import { DOCUMENT } from "@angular/common";
import { Component, EventEmitter, Inject, OnInit, Output, ViewChild } from "@angular/core";
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidationErrors,
} from "@angular/forms";
import { Router } from "@angular/router";
import { Subscription } from "rxjs";
import { delay } from "rxjs/operators";
import {
  ENVIRONMENT,
  FLIGHT_SEARCH_FORM,
  PAYMENT_NOTIFICATION_MESSAGE,
  ROUTING_CODES,
} from "src/app/_constants/app.constants";
import { MONTHS } from "src/app/_constants/months.constants";
import { ObjectLiteral } from "src/app/_models/user";
import { AirportsService } from "src/app/_services/api/airports/airports.service";
import { HelpOptionsService } from "src/app/_services/api/help-options/help-options.service";
import { FlightService } from 'src/app/_services/api/flight/flight.service';
import { LanguageService } from 'src/app/_services/api/language/language.service';
import { LoggerService } from 'src/app/_services/ui/logger/logger.service';
import { LoadingService } from 'src/app/_services/ui/loading/loading.service';
import { SessionService } from 'src/app/_services/session/session.service';
import { SessionUtilityService } from "src/app/_services/session/session/session-utility/session-utility.service";
import { WelcomeService } from "../_services/welcome.service";

@Component({
  selector: "ua-flight-search-form",
  templateUrl: "./flight-search-form.component.html",
  styleUrls: ["./flight-search-form.component.scss"],
})
export class FlightSearchFormComponent implements OnInit {

  readonly paymentMessage = PAYMENT_NOTIFICATION_MESSAGE;

  //common
  isLastNameInputFocused: boolean = false;

  //language constants
  languageSubscription: Subscription;
  pageConstants: ObjectLiteral = {};
  commonConstants: ObjectLiteral = {};

  //error constants
  errorMessage: any;
  showErrorMessage: boolean = false;

  //form constants
  lastName = FLIGHT_SEARCH_FORM.CONTROLS.LAST_NAME;
  destination = FLIGHT_SEARCH_FORM.CONTROLS.DESTINATION;
  dateOfBirth = FLIGHT_SEARCH_FORM.CONTROLS.DATE_OF_BIRTH;
  readonly months = MONTHS;
  isYearFieldFocused: boolean;

  //loader constants
  isLoading = true;

  //destination
  filteredAirports: ObjectLiteral[];
  keyword: string = "name";
  destinationDisplay: string = "";
  @ViewChild("finalDestination") finalDestination;
  @Output() errorMessageEmitter = new EventEmitter<string>();


  //rtc
  info: string = "";

  //failed search
  toAcceptFailedSearch: number = 0;
  selectedAirport: any;

  //min query length
  minQueryLength = FLIGHT_SEARCH_FORM.CONTROLS.DESTINATION.searchMinLength;

  destinationPlaceholder: any;
  lastNamePlaceHolder: any;

  /* ----- START :: Date field validation methods ----- */

  restrictInvalidChars = (event: any): boolean =>
    ["e", "E", "+", "-"].includes(event.key) ? false : true;

  limitChars = (maxLength: number, event: any): boolean =>
    event.target.value.toString().length == maxLength ? event.key == "Enter" ? true : false : true;

  resetMaxValue = (control: FormControl, maxValue: number): void =>
    control.value > maxValue && control.patchValue(maxValue);

  dateValidator = (control: AbstractControl): ValidationErrors | null => {
    const year = control.get("year")?.value;
    const day = control.get("day")?.value;
    const month = control.get("month")?.value;
    const date = new Date(`${month}/${day}/${year}`);
    if (!day || !month || !year) {
      return null;
    }
    if (
      date &&
      date.getFullYear() == year &&
      date.getMonth() + 1 == month &&
      date.getDate() == day &&
      date.getTime() < new Date().getTime()
    ) {
      return null;
    }
    return { invalidDate: { value: control.value } };
  };
  /* ----- END :: Date field validation methods ----- */

  /* ----- START :: Destination field validator ------- */

  destinationSelectValidator = (control: AbstractControl): ValidationErrors | null => {
    const destination = control?.value;
    if (this.selectedAirport) {
      let airportToMatch = `${this.selectedAirport.shortName?.split(",")[0]} ${this.selectedAirport.iataCode}`
      if (airportToMatch == destination) return null;
    }
    if (this.sessionService.easySearchData?.destination == destination) return null;
    return { notChosen: true };
  }
  /* ----- END :: Destination field validator ------- */

  //form initialization
  flightSearchForm = new FormGroup({
    destination: new FormControl("", [this.destinationSelectValidator, ...this.destination.validators]),
    lastName: new FormControl("", this.lastName.validators),
    dateOfBirth: new FormGroup(
      {
        year: new FormControl("", this.dateOfBirth.year.validators),
        month: new FormControl("", this.dateOfBirth.month.validators),
        day: new FormControl("", this.dateOfBirth.day.validators),
      },
      { validators: this.dateValidator }
    ),
  });

  //form getters
  get lastNameControl(): FormControl {
    return this.flightSearchForm.get("lastName") as FormControl;
  }

  get destinationControl(): FormControl {
    return this.flightSearchForm.get("destination") as FormControl;
  }

  get yearControl(): FormControl {
    return this.flightSearchForm.get("dateOfBirth.year") as FormControl;
  }

  get dayControl(): FormControl {
    return this.flightSearchForm.get("dateOfBirth.day") as FormControl;
  }

  get monthControl(): FormControl {
    return this.flightSearchForm.get("dateOfBirth.month") as FormControl;
  }

  get dateOfBirthFormControl(): FormGroup {
    return this.flightSearchForm.get("dateOfBirth") as FormGroup;
  }

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private languageService: LanguageService,
    private logger: LoggerService,
    private router: Router,
    private flightService: FlightService,
    private sessionService: SessionService,
    private airportsService: AirportsService,
    private loadingService: LoadingService,
    private helpOptionsService: HelpOptionsService,
    private sessionUtility: SessionUtilityService,
    private welcomseService: WelcomeService,
  ) {
    this.languageSubscription = this.languageService.languageChanges.subscribe(
      () => {
        this.pageConstants = this.languageService.constants?.home || {};
        this.commonConstants = this.languageService.constants?.commons || {};
        this.destinationPlaceholder = this.languageService.constants?.home.destinationPlaceHolder || '';
        this.lastNamePlaceHolder = this.languageService.constants?.home.lastNamePlaceHolder || '';
        this.logger.debug('FlightSearchFormComponent.constructor', this);
      }
    );
    this.flightService.clearFlightDetails();
  }

  ngOnInit(): void {
    this.populateSearchForm();
    if (this.flightService.showBoardingPass) {
      const { pnr_no, last_name } = this.sessionService.pnrDetails;
      this.flightService.isHelpRequested = true;
      this.submitPnrSearch(pnr_no, last_name);
    }
    this.info = FLIGHT_SEARCH_FORM.MESSAGES.INFO_NO_SCAN;
  }

  populateSearchForm() {
    if (this.sessionService.easySearchData.lastName) {
      this.flightSearchForm.patchValue(this.sessionService.easySearchData);
    }
  }

  async onFormSubmit() {
    this.errorMessageEmitter.emit('');
    const { lastName, dateOfBirth, destination } = this.flightSearchForm.value;
    const { year, month, day } = dateOfBirth;
    this.sessionService.userData.lastName = lastName;
    this.sessionService.easySearchData = { lastName, dateOfBirth: { year, month, day }, destination }
    this.logger.debug("easy Search Data :: ", this.sessionService.easySearchData);
    const dob = new Date(Date.UTC(Number.parseInt(year), Number.parseInt(month) - 1, Number.parseInt(day)))
      .toISOString()
      .split("T")[0];
    this.flightService.isHelpRequested = true;
    this.sessionService.isEasyPnrSearch = true;
    this.sessionService.showFlightLookupForm = false;
    try {
      await this.flightService.searchFlightDetails(
        lastName,
        destination.split(" ").pop(),
        dob
      );
      this.flightService.customerDetails
        ? this.navigateToAdditionalInfo()
        : this.configureRoute();
    } catch (e) {
      this.toAcceptFailedSearch += 1;

      this.sessionService.removePnrDetails();
      this.sessionService.showFlightLookupForm = true;

      this.welcomseService.showFlightLookupFormSubject.next({ action: 'assign', data: true });
      this.welcomseService.showBackButtonSubject.next({ action: 'assign', data: true });
      if (this.sessionService.userData.lastName) {
        this.welcomseService.flightLookupFormSubject.next({ action: 'patchValue', data: { lastName: this.sessionService.userData.lastName } })

        this.welcomseService.pnrControlSubject.next({ action: 'reset', data: {} })
      }

      this.errorMessage = FLIGHT_SEARCH_FORM.MESSAGES.ERROR;
      this.showErrorMessage = true;
      this.errorMessageEmitter.emit(this.errorMessage);
      this.logger.error("FlightSearchFormComponent: onFormSubmit()", e);
      this.document.defaultView.scrollTo(0, 0);
    }
  }

  configureRoute() {
    this.assignPnrDetailsInService(
      this.flightService.flightDetails.confirmationID,
      this.lastNameControl.value
    );
    this.sessionService.shouldSubmitPNR = false;
    this.flightService.activeTraveler == null
      ? this.navigateToTravelerSelection()
      : this.navigateToHelpOptions();
  }

  navigateToTravelerSelection(): void {
    this.router.navigate([ROUTING_CODES.TRAVELER_SELECTION.ROUTE], {
      replaceUrl: true,
    });
  }

  navigateToAdditionalInfo(): void {
    this.router.navigate([ROUTING_CODES.ADD_INFO.ROUTE], { replaceUrl: true });
  }

  async navigateToHelpOptions() {
    await this.helpOptionsService.getHelpOptions();
    this.router.navigate([ROUTING_CODES.HELP_OPTIONS.ROUTE], {
      replaceUrl: true,
    });
  }

  assignPnrDetailsInService(pnr: string, lastName: string): void {
    this.sessionService.pnrDetails = { pnr_no: pnr, last_name: lastName };
  }

  async navigateToPreChat() {
    await this.helpOptionsService.getHelpOptions();
    this.sessionUtility.configureSessionData();
    this.router.navigate([ROUTING_CODES.PRE_CHAT.ROUTE], { replaceUrl: true });
  }

  async submitPnrSearch(pnr: string, lastName: string) {
    this.lastNameControl.setValue(lastName);
    try {
      await this.flightService.getFlightDetails(
        pnr,
        lastName,
        this.sessionService.locationData.stationId
      );
      this.flightService.isHelpRequested = true;
      this.configureRoute();
    } catch (e) {
      this.errorMessage = FLIGHT_SEARCH_FORM.MESSAGES.ERROR;
      this.showErrorMessage = true;
      this.errorMessageEmitter.emit(this.errorMessage);
      this.logger.debug("Flight search boarding pass scanner failed :: ", e);
      this.document.defaultView.scrollTo(0, 0);
    }
  }
  /* ------- END :: Scanner Util methods --------- */

  /**
   * Listen to the loadingSub property in the LoadingService class. This drives the
   * display of the loading spinner.
   */
  listenToLoading(): void {
    this.loadingService.loadingSub
      .pipe(delay(0)) // This prevents a ExpressionChangedAfterItHasBeenCheckedError for subsequent requests
      .subscribe(({ loading }) => {
        this.isLoading = loading;
      });
  }

  /**
   * This method is responsible for fetching the data from service when updating the autocomplete field
   * @param value
   */
  onDestinationChange(value: string): void {
    this.destinationDisplay = "";
    if (!value || value?.length < this.minQueryLength) {
      this.finalDestination.close();
      this.filteredAirports = [];
    } else {
      if (value?.length <= FLIGHT_SEARCH_FORM.CONTROLS.DESTINATION.maxLength) {
        this.filteredAirports = this.airportsService.filterAirports(
          value,
          this.destination.datListLength
        );
      }
    }
  }

  /**
   * This method is fired when destination field is on focus
   * @param event
   */
  onInputFocused(event): void {
    event.target.blur = () => { }; //safari tabbing issue fix
    if (event && event.target && !event?.target.value) {
      this.resetDestination();
    }
  }

  /**
   * This method is fired when user selects one of the item from the autocomplete dropdown
   * @param value
   */

  onSelectEvent(value: ObjectLiteral): void {
    if (value.shortName && value.iataCode) {
      this.selectedAirport = value;
      this.destinationDisplay = `${value.shortName?.split(",")[0]} ${value.iataCode}`;
    }
  }

  /**
   * This method is fired when destination field is cleared
   * @param event
   */
  inputCleared(event): void {
    this.resetDestination();
  }

  /**
   * This method resets the destination form field
   */
  resetDestination(): void {
    this.finalDestination.close();
    this.destinationDisplay = "";
    this.filteredAirports = [];
  }

  /**
   * Auto fill for development purpose
   */
  isMock = ENVIRONMENT.MOCK
  checkValue() {
    this.flightSearchForm.setValue(
      {
        "destination": "Anaa AAA",
        "lastName": "aaa",
        "dateOfBirth": {
          "year": "2000",
          "month": "12",
          "day": "22"
        }
      }
    )
    // this.flightSearchForm
    this.isLoading = false;
  }
}
